import * as AvatarPrimitive from '@radix-ui/react-avatar'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * Avatar — user/entity representation.
 * Pencil: Avatar/{Small,Medium,Large,Icon} (j6TM9 88xFM EI5PR dtary)
 */
export const avatarVariants = cva(
  'relative inline-flex shrink-0 overflow-hidden rounded-pill bg-ia-elevated text-ia-text',
  {
    variants: {
      size: {
        xs: 'h-6 w-6 text-[10px]',
        sm: 'h-8 w-8 text-[11px]',
        md: 'h-10 w-10 text-body-sm',
        lg: 'h-12 w-12 text-body-md',
        xl: 'h-16 w-16 text-heading-sm',
      },
    },
    defaultVariants: { size: 'md' },
  },
)

export interface AvatarProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    VariantProps<typeof avatarVariants> {
  src?: string
  alt?: string
  initials?: string
}

export const Avatar = React.forwardRef<React.ElementRef<typeof AvatarPrimitive.Root>, AvatarProps>(
  ({ className, size, src, alt, initials, ...props }, ref) => (
    <AvatarPrimitive.Root ref={ref} className={cn(avatarVariants({ size }), className)} {...props}>
      {src && (
        <AvatarPrimitive.Image
          src={src}
          alt={alt ?? ''}
          className="aspect-square h-full w-full object-cover"
        />
      )}
      <AvatarPrimitive.Fallback className="flex h-full w-full items-center justify-center font-semibold">
        {initials ?? '?'}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  ),
)
Avatar.displayName = 'Avatar'
